/**
 * Security — replaces the /security ComingSoonPage stub (contest brief,
 * 2026-07-11).
 *
 * Plain-language security page drawn from the canonical privacy promise
 * (SONSPEAK-CANON block 1 — quoted, never paraphrased) and the internal
 * third-party data audit. Rules honored here:
 *   - No certifications claimed, no legal-compliance claims — "designed
 *     for", never "compliant with".
 *   - Server amnesia stated WITH its one honest exception, exactly as
 *     CANON states it.
 *   - No "verified" / "sync" / "free" / medical-acronym language.
 */
import { Link } from "react-router-dom";
import {
  ShieldCheck, Smartphone, Server, EyeOff, AlertTriangle, Mail,
} from "lucide-react";
import { useSEO } from "@/lib/seo";

const FIVE_SERVICES = [
  {
    name: "Account & consent",
    sees: "The adult's identity only — your email, your password (hashed), your consent records. Never the child's.",
  },
  {
    name: "Credit ledger",
    sees: "Numbers. How many credits your account holds and spends — no content, by design.",
  },
  {
    name: "Helper broker",
    sees: "Caregiver-approved, sanitized, generic briefs in transit to outside AI helpers. It retains nothing.",
  },
  {
    name: "Encrypted relay",
    sees: "Ciphertext it cannot read, passing suggestions between you and professionals you invited.",
  },
  {
    name: "Release manifest",
    sees: "Plain app configuration — version numbers and settings. No family data at all.",
  },
];

export default function Security() {
  useSEO({
    title: "Security",
    description:
      "How SonSpeak keeps your child's data private by physics: everything " +
      "the family creates lives encrypted on your device, and no service " +
      "of ours can read it.",
    path: "/security",
  });

  return (
    <div className="min-h-screen bg-[color:var(--cream)]" data-testid="security-page">
      {/* Hero */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-8">
        <p className="display text-xs font-bold uppercase tracking-[0.24em] text-[color:var(--coral-deep)] mb-3">
          Security
        </p>
        <h1 className="display text-3xl sm:text-4xl font-extrabold leading-tight mb-4">
          Private by physics, not by policy.
        </h1>
        <p className="text-base sm:text-lg text-[color:var(--ink)]/80 leading-relaxed mb-6">
          Most apps promise not to look at your data. SonSpeak is designed so
          there is nothing to look at. The promise, word for word:
        </p>
        <blockquote
          className="brutal-card bg-[color:var(--paper)] p-5 display text-lg sm:text-xl font-extrabold leading-snug"
          data-testid="security-privacy-quote"
        >
          &ldquo;Zero remotely readable identifying child content.&rdquo;
        </blockquote>
      </section>

      {/* On the device */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-8">
        <div className="brutal-card bg-white p-6">
          <h2 className="display text-xl font-extrabold mb-3 flex items-center gap-2">
            <Smartphone size={20} aria-hidden /> What lives on your device
          </h2>
          <p className="text-sm sm:text-base text-[color:var(--ink)]/85 leading-relaxed mb-3">
            Everything your child or family creates lives encrypted on your
            device: boards, tile photos, voice recordings, button-press
            history, your child's name and settings. It is designed never to
            be readable by anyone else — including us.
          </p>
          <p className="text-sm sm:text-base text-[color:var(--ink)]/85 leading-relaxed">
            The child's tablet runs Board Only mode: tiles and nothing else.
            <strong> The child's screen has zero AI, by architecture</strong> —
            there is no AI surface on that device to secure, because none
            exists.
          </p>
        </div>
      </section>

      {/* The five services */}
      <section className="border-y-[3px] border-[color:var(--ink)] bg-[color:var(--paper)]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-12">
          <h2 className="display text-xl sm:text-2xl font-extrabold mb-2 flex items-center gap-2">
            <Server size={20} aria-hidden /> Exactly five remote services exist
          </h2>
          <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed mb-6">
            Not &ldquo;a cloud.&rdquo; Five small, named services — and none of
            them can read a child's name, photo, voice, board, or history.
          </p>
          <ol className="space-y-3">
            {FIVE_SERVICES.map((svc, i) => (
              <li
                key={svc.name}
                className="brutal-card bg-white p-4 flex items-start gap-3"
                data-testid={`security-service-${i + 1}`}
              >
                <span className="shrink-0 w-8 h-8 rounded-lg border-[3px] border-[color:var(--ink)] bg-[color:var(--gold)] flex items-center justify-center display font-extrabold text-sm">
                  {i + 1}
                </span>
                <div>
                  <h3 className="display text-sm font-extrabold mb-0.5">{svc.name}</h3>
                  <p className="text-sm text-[color:var(--ink-soft)] leading-snug">{svc.sees}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Server amnesia + the unconditional line */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <div className="space-y-4">
          <div className="brutal-card bg-white p-6">
            <h2 className="display text-xl font-extrabold mb-3 flex items-center gap-2">
              <ShieldCheck size={20} aria-hidden /> The server-amnesia test
            </h2>
            <p className="text-sm sm:text-base text-[color:var(--ink)]/85 leading-relaxed mb-3">
              Wipe every server we run, and no family loses their data —
              boards, photos, and voices are still on your device, untouched.
            </p>
            <p className="text-sm sm:text-base text-[color:var(--ink)]/85 leading-relaxed">
              The sole exception, stated honestly: professional suggestions
              still in flight on the relay — undelivered encrypted messages
              held up to 72 hours — would be lost and would need to be
              re-sent. That's the entire blast radius.
            </p>
          </div>
          <div className="brutal-card bg-white p-6">
            <h2 className="display text-xl font-extrabold mb-3 flex items-center gap-2">
              <EyeOff size={20} aria-hidden /> The unconditional line
            </h2>
            <p className="text-sm sm:text-base text-[color:var(--ink)]/85 leading-relaxed">
              Custom text about your child never leaves the device. Not to
              outside AI helpers, not with parental consent on file —
              <strong> never</strong>. When outside AI helpers are used, they
              receive only caregiver-approved briefs drawn from a closed,
              generic vocabulary; your custom words serve your device alone.
            </p>
          </div>
          <div className="brutal-card bg-white p-6">
            <h2 className="display text-xl font-extrabold mb-3">
              What we deliberately don't run
            </h2>
            <ul className="space-y-2 text-sm sm:text-base text-[color:var(--ink)]/85 leading-relaxed list-disc list-inside">
              <li>No advertising SDKs, ad pixels, or remarketing tags.</li>
              <li>No session-recording or screen-replay tools.</li>
              <li>No third-party support widgets that can see your screen — support is email only.</li>
              <li>No fonts or assets fetched from tracking CDNs — assets are served first-party.</li>
              <li>Errors write to a local log that leaves the device only by the caregiver's own hand.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Honest limits */}
      <section className="border-t-[3px] border-[color:var(--ink)] bg-[color:var(--paper)]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-12">
          <h2 className="display text-xl sm:text-2xl font-extrabold mb-4 flex items-center gap-2">
            <AlertTriangle size={20} aria-hidden /> What we don't claim
          </h2>
          <div className="space-y-3 text-sm sm:text-base text-[color:var(--ink)]/85 leading-relaxed">
            <p>
              We hold no security certifications and we won't pretend
              otherwise — no audit stamp appears on this page because none
              has been earned yet. SonSpeak is <strong>designed for</strong>{" "}
              the standards children's-privacy law expects; we do not claim
              legal compliance that no lawyer has signed.
            </p>
            <p>
              SonSpeak is in early access. If you find a security problem, we
              genuinely want to know:{" "}
              <a
                href="mailto:privacy@sonspeak.com"
                className="underline font-semibold"
                data-testid="security-report-email"
              >
                privacy@sonspeak.com
              </a>{" "}
              reaches the founder directly.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            <Link to="/privacy-promise" className="pill-btn text-sm" data-testid="security-promise-link">
              The full privacy promise
            </Link>
            <Link to="/data-protection" className="pill-btn text-sm" data-testid="security-dp-link">
              Data protection details
            </Link>
            <a href="mailto:privacy@sonspeak.com" className="pill-btn text-sm">
              <Mail size={14} aria-hidden /> Report an issue
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
